// Suite-consistent auth + runtime config, mirroring magnify-app:
// JWT arrives via the URL hash (#access=&refresh=), is kept in MEMORY only (never
// localStorage), expiry is checked client-side, and a silent refresh runs against
// brainherd-auth. getToken() is the accessTokenFactory the SignalR/command transport uses.

const params = new URLSearchParams(location.search)

/** Live API base (Functions). When absent the app runs on the mock source.
 *  Override at runtime for testing with ?api=https://host/api . */
export const apiBase: string | undefined =
  params.get('api') ?? (import.meta.env.VITE_API_BASE as string | undefined) ?? undefined
export const siteGuid: string =
  params.get('site') ?? (import.meta.env.VITE_SITE_GUID as string | undefined) ?? 'dev-site'
export const extension: string =
  params.get('ext') ?? (import.meta.env.VITE_EXTENSION as string | undefined) ?? '1042'
export const loginUrl: string =
  (import.meta.env.VITE_LOGIN_URL as string | undefined) ?? 'https://magnify-app.brainherd.workers.dev/login.html'

const AUTH_REFRESH_URL = 'https://brainherd-auth.azurewebsites.net/api/auth/refresh'

interface AuthWindow { __sbJwt?: string | null; __sbRefresh?: string | null }
const w = window as unknown as AuthWindow

let jwt: string | null = w.__sbJwt ?? params.get('token') ?? null
let refreshToken: string | null = w.__sbRefresh ?? null

function decodeExp(token: string): number {
  try {
    const p = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')))
    return typeof p.exp === 'number' ? p.exp : 0
  } catch { return 0 }
}

async function tryRefresh(): Promise<boolean> {
  if (!refreshToken) return false
  try {
    const r = await fetch(AUTH_REFRESH_URL, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken })
    })
    if (!r.ok) return false
    const d = (await r.json()) as { access_token?: string; refresh_token?: string }
    if (d.access_token) {
      jwt = d.access_token
      if (d.refresh_token) refreshToken = d.refresh_token
      return true
    }
    return false
  } catch { return false }
}

/** accessTokenFactory: returns a currently-valid JWT, refreshing silently within 60s of expiry. */
export async function getToken(): Promise<string> {
  if (!jwt) return ''
  const exp = decodeExp(jwt)
  if (exp && Math.floor(Date.now() / 1000) > exp - 60) await tryRefresh()
  return jwt ?? ''
}

export function hasToken(): boolean {
  if (!jwt) return false
  const exp = decodeExp(jwt)
  return !exp || Math.floor(Date.now() / 1000) < exp
}

export const isLive = (): boolean => !!apiBase

/** Send the user to the suite login, asking it to return here. */
export function goToLogin(): void {
  const url = `${loginUrl}?return=${encodeURIComponent(location.href)}`
  location.href = url
}
